export const periodConfig = {
    month: { label: 'Month', title: 'Daily Profit — Last 30 Days', granularity: 'day' },
    ytd: { label: 'YTD', title: 'Daily Profit — Year to Date', granularity: 'day' },
    year: { label: 'Year', title: 'Weekly Profit — Last 52 Weeks', granularity: 'week' },
    alltime: { label: 'All Time', title: 'Monthly Profit — All Time', granularity: 'month' },
};
export const selectSeries = (pt, period) => {
    switch (period) {
        case 'month': return pt.daily ?? [];
        case 'ytd': return pt.dailyYtd ?? [];
        case 'year': return pt.weeklyYear ?? [];
        case 'alltime': return pt.monthlyAllTime ?? [];
    }
};
export const median = (values) => {
    if (values.length === 0)
        return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};
export const groupIntoWeeks = (daily) => {
    if (daily.length === 0)
        return [];
    const weeks = {};
    for (const d of daily) {
        const date = new Date(d.date);
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        const weekStart = new Date(date.setDate(diff)).toISOString().slice(0, 10);
        weeks[weekStart] = (weeks[weekStart] ?? 0) + d.profit;
    }
    return Object.values(weeks);
};
export const groupIntoMonths = (daily) => {
    if (daily.length === 0)
        return [];
    const months = {};
    for (const d of daily) {
        const key = d.date.slice(0, 7);
        months[key] = (months[key] ?? 0) + d.profit;
    }
    return Object.values(months);
};
export const toPct = (dollar, deposits) => deposits > 0 ? (dollar / deposits) * 100 : 0;
const buildMonthCards = (pt) => {
    const last30Profit = (pt.daily ?? []).reduce((s, d) => s + d.profit, 0);
    const last30Trades = (pt.daily ?? []).reduce((s, d) => s + d.trades, 0);
    const h24 = pt.last24Hours ?? pt.today;
    return [
        { title: 'Last 24 Hours', value: h24.profit, tradeCount: h24.trades },
        { title: 'This Week', value: pt.thisWeek.profit, tradeCount: pt.thisWeek.trades },
        { title: 'This Month', value: pt.thisMonth.profit, tradeCount: pt.thisMonth.trades },
        { title: 'Last 30 Days', value: last30Profit, tradeCount: last30Trades },
    ];
};
const buildYtdCards = (pt) => {
    const ytdProfit = pt.thisYear.profit;
    const ytdTrades = pt.thisYear.trades;
    const dailyYtd = pt.dailyYtd ?? [];
    const weeklyProfits = groupIntoWeeks(dailyYtd);
    const weeksElapsed = weeklyProfits.length || 1;
    const avgPerWeek = ytdProfit / weeksElapsed;
    const medPerWeek = median(weeklyProfits);
    const now = new Date();
    const yearEnd = new Date(now.getFullYear(), 11, 31);
    const msRemaining = yearEnd.getTime() - now.getTime();
    const weeksRemaining = Math.max(0, msRemaining / (7 * 24 * 60 * 60 * 1000));
    const projAvg = ytdProfit + avgPerWeek * weeksRemaining;
    const projMed = ytdProfit + medPerWeek * weeksRemaining;
    const monthlyProfits = groupIntoMonths(dailyYtd);
    const monthsElapsed = monthlyProfits.length || 1;
    const avgPerMonth = ytdProfit / monthsElapsed;
    const medPerMonth = median(monthlyProfits);
    return [
        { title: 'Year to Date', value: ytdProfit, tradeCount: ytdTrades },
        {
            title: 'Projected by Year End',
            value: projMed,
            valueSuffix: 'by med',
            subtitleTemplate: '{v} by avg',
            subtitleValue: projAvg,
        },
        { title: 'Avg per Month', value: avgPerMonth, subtitleTemplate: '{v} med', subtitleValue: medPerMonth },
        { title: 'Avg per Week', value: avgPerWeek, subtitleTemplate: '{v} med', subtitleValue: medPerWeek },
    ];
};
const buildYearCards = (pt, totalDeposits) => {
    const monthlyData = pt.monthlyYear ?? [];
    const weeklyData = pt.weeklyYear ?? [];
    const totalProfit = monthlyData.reduce((s, d) => s + d.profit, 0);
    const totalTrades = monthlyData.reduce((s, d) => s + d.trades, 0);
    const monthProfits = monthlyData.map((d) => d.profit);
    const monthCount = monthProfits.length || 1;
    const avgPerMonth = totalProfit / monthCount;
    const medPerMonth = median(monthProfits);
    const weekProfits = weeklyData.map((d) => d.profit);
    const bestWeek = weekProfits.length > 0 ? Math.max(...weekProfits) : 0;
    const bestWeekEntry = weeklyData.find((d) => d.profit === bestWeek);
    const bestWeekDate = bestWeekEntry
        ? new Date(bestWeekEntry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';
    return [
        { title: 'Last 12 Months', value: totalProfit, tradeCount: totalTrades },
        {
            title: 'Profit %',
            value: totalProfit,
            forcePercent: true,
            percentBase: totalDeposits,
            subtitleTemplate: 'of {v} deposits',
            subtitleValue: totalDeposits,
        },
        { title: 'Avg per Month', value: avgPerMonth, subtitleTemplate: '{v} med', subtitleValue: medPerMonth },
        { title: 'Best Week', value: bestWeek, subtitleText: bestWeekDate ? `week of ${bestWeekDate}` : 'no data' },
    ];
};
const buildAllTimeCards = (pt, totalDeposits) => {
    const monthlyData = pt.monthlyAllTime ?? [];
    const totalProfit = pt.allTime?.profit ?? monthlyData.reduce((s, d) => s + d.profit, 0);
    const totalTrades = pt.allTime?.trades ?? monthlyData.reduce((s, d) => s + d.trades, 0);
    const monthProfits = monthlyData.map((d) => d.profit);
    const monthCount = monthProfits.length || 1;
    const avgPerMonth = totalProfit / monthCount;
    const medPerMonth = median(monthProfits);
    const bestMonth = monthProfits.length > 0 ? Math.max(...monthProfits) : 0;
    const bestMonthEntry = monthlyData.find((d) => d.profit === bestMonth);
    const bestMonthDate = bestMonthEntry
        ? new Date(bestMonthEntry.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : '';
    return [
        { title: 'All Time Profit', value: totalProfit, tradeCount: totalTrades },
        {
            title: 'Profit %',
            value: totalProfit,
            forcePercent: true,
            percentBase: totalDeposits,
            subtitleTemplate: 'of {v} deposits',
            subtitleValue: totalDeposits,
        },
        { title: 'Avg per Month', value: avgPerMonth, subtitleTemplate: '{v} med', subtitleValue: medPerMonth },
        { title: 'Best Month', value: bestMonth, subtitleText: bestMonthDate || 'no data' },
    ];
};
export const buildTrendCards = (pt, period, totalDeposits) => {
    switch (period) {
        case 'month': return buildMonthCards(pt);
        case 'ytd': return buildYtdCards(pt);
        case 'year': return buildYearCards(pt, totalDeposits);
        case 'alltime': return buildAllTimeCards(pt, totalDeposits);
    }
};
